import type { Editor } from '@tiptap/core';
import type { SuggestionProps } from '@tiptap/suggestion';
import * as React from 'react';
import { ISupportSavedReply } from '../../../../models/Support/ISupportSavedReply';
import { ISupportTicket } from '../../../../models/Support/ISupportTicket';
import { substituteVariables } from '../../../../models/Support/savedReplySubstitution';
import { useAgentConfigsQuery } from '../../../../queries/Support/useSupportAgentConfig';
import { useSavedRepliesQuery } from '../../../../queries/Support/useSupportSavedReplies';
import { getInitials, getSupportUserProfilePicSrc } from '../../../../utils/Support/supportHelpers';
import type {
	MentionCommandAttributes,
	MentionOption,
	SuggestionKind,
	SuggestionMenuItem,
	SuggestionMenuState,
} from './types';
import type { ComposerEditorCallbacks } from './useComposerEditor';

type SlashSuggestionSession = {
	kind: 'slash';
	command: (reply: ISupportSavedReply) => void;
	range: { from: number; to: number };
};

type MentionSuggestionSession = {
	kind: 'mention';
	command: (attrs: MentionCommandAttributes) => void;
	range: { from: number; to: number };
};

type SuggestionSession = SlashSuggestionSession | MentionSuggestionSession;

interface UseComposerSuggestionsOptions {
	agentName: string;
	currentAccountId?: string;
	currentUserId: string;
	editorWrapRef: React.RefObject<HTMLDivElement>;
	ticket: ISupportTicket;
}

type SuggestionEditorCallbacks = Pick<
	ComposerEditorCallbacks,
	'onEditorReady' | 'insertSavedReply' | 'onSuggestionChange' | 'onSuggestionKeyDown'
>;

const stripHtml = (html: string): string =>
	html
		.replace(/<[^>]+>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

export const useComposerSuggestions = ({
	agentName,
	currentAccountId,
	currentUserId,
	editorWrapRef,
	ticket,
}: UseComposerSuggestionsOptions) => {
	const [menu, setMenu] = React.useState<SuggestionMenuState | null>(null);
	const menuStateRef = React.useRef<SuggestionMenuState | null>(menu);
	const suggestionSessionRef = React.useRef<SuggestionSession | null>(null);
	const editorRef = React.useRef<Editor | null>(null);
	const menuElRef = React.useRef<HTMLDivElement>(null);
	const savedRepliesButtonRef = React.useRef<HTMLButtonElement>(null);
	const slashItemsRef = React.useRef<ISupportSavedReply[]>([]);
	const mentionItemsRef = React.useRef<MentionOption[]>([]);
	const ticketRef = React.useRef(ticket);
	ticketRef.current = ticket;

	const setMenuState = React.useCallback((nextMenu: SuggestionMenuState | null) => {
		menuStateRef.current = nextMenu;
		setMenu(nextMenu);
	}, []);

	const positionMenu = React.useCallback(
		(kind: SuggestionKind, clientRect?: (() => DOMRect | null) | null) => {
			const rect = clientRect?.();
			const wrapRect = editorWrapRef.current?.getBoundingClientRect();
			if (!rect || !wrapRect) return { x: 16, y: 40 };
			const menuWidth = kind === 'slash' ? 330 : 320;
			return {
				x: Math.max(6, Math.min(rect.left - wrapRect.left, Math.max(6, wrapRect.width - menuWidth))),
				y: rect.bottom - wrapRect.top + 4,
			};
		},
		[editorWrapRef]
	);

	const closeMenu = React.useCallback(
		(reason: 'dismiss' | 'finish' = 'dismiss') => {
			const currentMenu = menuStateRef.current;
			const currentSession = suggestionSessionRef.current;
			if (reason === 'dismiss' && currentMenu?.source === 'typed' && currentMenu.kind === 'slash' && currentSession) {
				editorRef.current?.chain().deleteRange(currentSession.range).run();
			}
			suggestionSessionRef.current = null;
			setMenuState(null);
		},
		[setMenuState]
	);

	const handleSuggestionChange = React.useCallback(
		(kind: SuggestionKind, props: SuggestionProps | null) => {
			if (!props) {
				suggestionSessionRef.current = null;
				const currentMenu = menuStateRef.current;
				if (currentMenu?.source === 'typed' && currentMenu.kind === kind) {
					setMenuState(null);
				}
				return;
			}

			const currentMenu = menuStateRef.current;
			suggestionSessionRef.current =
				kind === 'slash'
					? { kind, command: props.command as SlashSuggestionSession['command'], range: props.range }
					: { kind, command: props.command as MentionSuggestionSession['command'], range: props.range };
			setMenuState({
				kind,
				query: props.query,
				index: currentMenu?.kind === kind && currentMenu.source === 'typed' ? currentMenu.index : 0,
				...positionMenu(kind, props.clientRect),
				source: 'typed',
			});
		},
		[positionMenu, setMenuState]
	);

	const insertSavedReply = React.useCallback(
		(instance: Editor, range: { from: number; to: number }, reply: ISupportSavedReply) => {
			const html = substituteVariables(reply.body, { ticket: ticketRef.current, agentName });
			instance.chain().focus().deleteRange(range).insertContent(html).run();
		},
		[agentName]
	);

	const pickSuggestion = React.useCallback(
		(index: number) => {
			const currentMenu = menuStateRef.current;
			if (!currentMenu) return;
			if (currentMenu.kind === 'mention') {
				const option = mentionItemsRef.current[index];
				const session = suggestionSessionRef.current;
				if (option && session?.kind === 'mention') {
					session.command({ id: option.userId, label: option.label });
				}
			} else {
				const reply = slashItemsRef.current[index];
				if (reply) {
					const html = substituteVariables(reply.body, { ticket: ticketRef.current, agentName });
					const session = suggestionSessionRef.current;
					if (currentMenu.source === 'typed' && session?.kind === 'slash') {
						session.command(reply);
					} else {
						editorRef.current?.chain().focus().insertContent(html).run();
					}
				}
			}
			closeMenu('finish');
		},
		[agentName, closeMenu]
	);

	const handleMenuNavigation = React.useCallback(
		(event: KeyboardEvent): boolean => {
			const currentMenu = menuStateRef.current;
			if (!currentMenu) return false;
			const count = currentMenu.kind === 'slash' ? slashItemsRef.current.length : mentionItemsRef.current.length;
			switch (event.key) {
				case 'ArrowDown':
				case 'ArrowUp': {
					if (count === 0) return true;
					const delta = event.key === 'ArrowDown' ? 1 : -1;
					setMenuState({ ...currentMenu, index: (currentMenu.index + delta + count) % count });
					return true;
				}
				case 'Enter':
				case 'Tab': {
					if (count > 0) pickSuggestion(Math.min(currentMenu.index, count - 1));
					return true;
				}
				case 'Escape': {
					closeMenu('dismiss');
					return true;
				}
				default:
					return false;
			}
		},
		[closeMenu, pickSuggestion, setMenuState]
	);

	const handleSuggestionKeyDown = React.useCallback(
		(kind: SuggestionKind, event: KeyboardEvent) => {
			const currentMenu = menuStateRef.current;
			if (!currentMenu || currentMenu.source !== 'typed' || currentMenu.kind !== kind) return false;
			return handleMenuNavigation(event);
		},
		[handleMenuNavigation]
	);

	const toggleSavedReplies = React.useCallback(() => {
		const currentMenu = menuStateRef.current;
		if (currentMenu?.kind === 'slash') {
			closeMenu('dismiss');
			return;
		}
		if (currentMenu) {
			closeMenu('dismiss');
		}
		editorRef.current?.commands.focus();
		setMenuState({ kind: 'slash', query: '', index: 0, x: 16, y: 10, source: 'toolbar' });
	}, [closeMenu, setMenuState]);

	const toolbarMenuOpen = menu?.source === 'toolbar';
	React.useEffect(() => {
		if (!toolbarMenuOpen) return;
		const handleKeyDown = (event: KeyboardEvent) => {
			if (handleMenuNavigation(event)) {
				event.preventDefault();
				event.stopPropagation();
			}
		};
		document.addEventListener('keydown', handleKeyDown, true);
		return () => document.removeEventListener('keydown', handleKeyDown, true);
	}, [handleMenuNavigation, toolbarMenuOpen]);

	const menuOpen = menu != null;
	React.useEffect(() => {
		if (!menuOpen) return;
		const handleMouseDown = (event: MouseEvent) => {
			const target = event.target as Node;
			if (menuElRef.current?.contains(target)) return;
			if (savedRepliesButtonRef.current?.contains(target)) return;
			closeMenu('dismiss');
		};
		document.addEventListener('mousedown', handleMouseDown);
		return () => document.removeEventListener('mousedown', handleMouseDown);
	}, [closeMenu, menuOpen]);

	const savedRepliesQuery = useSavedRepliesQuery(ticket.mailboxId || '');
	const savedReplies = React.useMemo(() => savedRepliesQuery.data?.values ?? [], [savedRepliesQuery.data?.values]);
	const filteredSavedReplies = React.useMemo(() => {
		if (menu?.kind !== 'slash') return [];
		const query = menu.query.toLowerCase();
		return savedReplies.filter(reply => !query || reply.name.toLowerCase().includes(query));
	}, [menu, savedReplies]);
	slashItemsRef.current = filteredSavedReplies;

	const agentConfigsQuery = useAgentConfigsQuery(ticket.mailboxId || '');
	const agentConfigs = agentConfigsQuery.data?.values;
	const mentionOptions = React.useMemo<MentionOption[]>(() => {
		if (menu?.kind !== 'mention') return [];
		const query = menu.query.toLowerCase();
		return (agentConfigs ?? [])
			.filter(agent => agent.agentId !== currentUserId && agent.agentName)
			.filter(agent => !query || agent.agentName?.toLowerCase().includes(query))
			.slice(0, 6)
			.map(agent => ({
				userId: agent.agentId,
				label: agent.agentName || 'Teammate',
				email: agent.agentEmail,
				profilePicSrc: getSupportUserProfilePicSrc({
					agentProfilePic: agent.agentProfilePic,
					userId: agent.agentId,
					accountId: currentAccountId,
				}),
			}));
	}, [agentConfigs, currentAccountId, currentUserId, menu]);
	mentionItemsRef.current = mentionOptions;

	const items = React.useMemo<SuggestionMenuItem[]>(() => {
		if (menu?.kind === 'mention') {
			return mentionOptions.map(option => ({
				key: option.userId,
				name: option.label,
				sub: option.email,
				avatarImgSrc: option.profilePicSrc,
				initials: getInitials(option.label, option.email),
			}));
		}
		return filteredSavedReplies.map(reply => ({
			key: reply.id,
			name: reply.name,
			sub: stripHtml(substituteVariables(reply.body, { ticket, agentName })),
		}));
	}, [agentName, filteredSavedReplies, mentionOptions, menu?.kind, ticket]);

	const editorCallbacks = React.useMemo<SuggestionEditorCallbacks>(
		() => ({
			onEditorReady: nextEditor => {
				editorRef.current = nextEditor;
			},
			insertSavedReply,
			onSuggestionChange: handleSuggestionChange,
			onSuggestionKeyDown: handleSuggestionKeyDown,
		}),
		[handleSuggestionChange, handleSuggestionKeyDown, insertSavedReply]
	);

	const close = React.useCallback(() => closeMenu('dismiss'), [closeMenu]);

	return {
		close,
		editorCallbacks,
		isOpen: menuOpen,
		isSavedRepliesActive: menu?.kind === 'slash',
		items,
		menu,
		menuRef: menuElRef,
		pick: pickSuggestion,
		savedRepliesButtonRef,
		toggleSavedReplies,
	};
};
